import React, { useEffect, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { CAREERS_LINK } from 'src/constants';
import { useScrollLock } from 'src/hooks';
import { breakpoint, pxToRem, spacing } from 'src/theme';
import { openIntercom } from 'src/utils';

import AdsIcon from 'src/images/icons/AdsIcon.svg';
import BizwiseIcon from 'src/images/icons/BizwiseIcon.svg';
import BlogIcon from 'src/images/icons/BlogIcon.svg';
import CareersIconAlt from 'src/images/icons/CareersIconAlt.svg';
import CustomerListIcon from 'src/images/icons/CustomerListIcon.svg';
import EmailIcon from 'src/images/icons/EmailIcon.svg';
import GuidesIcon from 'src/images/icons/GuidesIcon.svg';
import MarketingIcon from 'src/images/icons/MarketingIcon.svg';
import PeopleIcon from 'src/images/icons/PeopleIcon.svg';
import PersonPlusIcon from 'src/images/icons/PersonPlusIcon.svg';
import SecurityIcon from 'src/images/icons/SecurityIcon.svg';
import SupportIcon from 'src/images/icons/SupportIcon.svg';
import TeamsIcon from 'src/images/icons/TeamsIcon.svg';
import WebsiteIcon from 'src/images/icons/WebsiteIcon.svg';
import { IntentionalAny } from 'src/types';

export const navbarHeightPx = 75;
export const navbarHeight = pxToRem(navbarHeightPx);

export interface Link {
  icon?: React.JSXElementConstructor<IntentionalAny>,
  route?: string,
  label: string,
  items?: {
    icon?: React.JSXElementConstructor<IntentionalAny>,
    label: string,
    isExternal?: boolean,
    route?: string,
    onClick?: () => void,
  }[],
}

export const links: Link[] = [
  {
    route: '/',
    label: 'Home',
  },
  {
    label: 'Features',
    items: [
      {
        route: '/website/',
        label: 'Website',
        icon: WebsiteIcon,
      },
      {
        route: '/customer-list/',
        label: 'Customer List',
        icon: CustomerListIcon,
      },
      {
        route: '/email/',
        label: 'Email',
        icon: EmailIcon,
      },
      {
        route: '/teams/',
        label: 'Teams',
        icon: TeamsIcon,
      },
      {
        route: '/ads/',
        label: 'Ads',
        icon: AdsIcon,
      },
      {
        route: '/social/',
        label: 'Social',
        icon: MarketingIcon,
      },
    ],
  },
  {
    label: 'Company',
    items: [
      {
        route: '/about/',
        label: 'About Bizwise',
        icon: BizwiseIcon,
      },
      {
        route: CAREERS_LINK,
        label: 'Careers',
        icon: CareersIconAlt,
      },
      {
        icon: PersonPlusIcon,
        route: '/affiliate',
        label: 'Recommend Bizwise',
      },
    ],
  },
  {
    label: 'Resources',
    items: [
      {
        icon: PeopleIcon,
        route: '/customers/',
        label: 'Customers',
      },
      {
        icon: SupportIcon,
        onClick: openIntercom,
        label: 'Support',
      },
      {
        icon: GuidesIcon,
        route: 'https://guides.bizwise.com/',
        isExternal: true,
        label: 'Product Guides',
      },
      {
        route: '/policy/',
        label: 'Security & Data Protection',
        icon: SecurityIcon,
      },
      {
        route: '/business-center/',
        label: 'Blog',
        icon: BlogIcon,
      },
      // {
      //   route: '/contact',
      //   label: 'Contact',
      //   icon: PersonHeadphonesIcon,
      // },
    ],
  },
  {
    route: '/pricing/',
    label: 'Pricing',
  },
];

export function useNavbar() {
  const [isAtTop, setIsAtTop] = useState(true);
  useEffect(() => {
    setIsAtTop(window.pageYOffset <= 0);
    function onScroll() {
      setIsAtTop(window.pageYOffset <= 0);
    }
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return {
    isAtTop,
  };
}

export function useMobileNavbar() {
  const [open, setOpen] = useState(false);
  const [openedSubMenuIdx, setOpenedSubMenuIdx] = useState<number | null>(null);
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const { lockScroll, unlockScroll } = useScrollLock();

  useEffect(() => {
    if (openedSubMenuIdx != null) setSubMenuOpen(true);
  }, [openedSubMenuIdx]);

  function handleOpen() {
    setOpen(true);
    lockScroll();
  }
  function handleClose() {
    unlockScroll();
    setOpen(false);
    setOpenedSubMenuIdx(null);
    setSubMenuOpen(false);
  }
  return {
    open,
    handleOpen,
    handleClose,
    openedSubMenuIdx,
    setOpenedSubMenuIdx,
    subMenuOpen,
    setSubMenuOpen,
  };
}

interface StyleProps {
  isAtTop: boolean,
}

export function getNavBackgroundStyle({ isAtTop }: StyleProps) {
  return {
    background: 'linear-gradient(105.37deg, #13266D 16.05%, #5FBCFF 97.02%)',
    opacity: !isAtTop ? 1 : 0,
    transition: 'all 0.2s ease',
    width: '100%',
    height: '100%',
    position: 'absolute' as const,
    top: 0,
    left: 0,
  };
}

export const useCommonStyles = createUseStyles({
  nav: {
    height: navbarHeight,
    position: 'fixed',
    width: '100%',
    zIndex: 10,
  },
  logo: {
    height: pxToRem(30),
    [breakpoint.down('sm')]: {
      height: pxToRem(30),
    },
    marginTop: '-2%',
  },
  navbarShape: {
    mixBlendMode: 'overlay',
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    pointerEvents: 'none',
  },
  links: {
    display: 'flex',
    alignItems: 'center',
    '& > *': {
      [breakpoint.up('xl')]: {
        marginLeft: spacing(12.5),
      },
      [breakpoint.between('lg', 'xl')]: {
        marginLeft: spacing(10),
      },
      [breakpoint.between('mdlg', 'lg')]: {
        marginLeft: spacing(8),
      },
      [breakpoint.between('md', 'mdlg')]: {
        marginLeft: spacing(6.5),
      },
      [breakpoint.between('sm', 'md')]: {
        marginLeft: spacing(2),
      },
      [breakpoint.down('sm')]: {
        marginLeft: spacing(2),
      },
    },
  },
  buttons: {
    marginLeft: 'auto',
  },
  contentLayer: {
    display: 'flex',
    alignItems: 'center',
    height: '100%',

    paddingLeft: 0,
    paddingRight: 0,
    [breakpoint.up('xl')]: {
      paddingLeft: '8vw',
      paddingRight: '8vw',
    },
    [breakpoint.between('lg', 'xl')]: {
      paddingLeft: '4vw',
      paddingRight: '4vw',
    },
    [breakpoint.between('md', 'lg')]: {
      paddingLeft: '4vw',
      paddingRight: '4vw',
    },
    [breakpoint.between('sm', 'md')]: {
      paddingLeft: '4vw',
      paddingRight: '4vw',
    },
    [breakpoint.down('sm')]: {
      paddingLeft: '2vw',
      paddingRight: '2vw',
    },

    '& *': {
      zIndex: 1,
    },
  },
});
